import { useEffect, useState } from "react";
import { Badge } from "../ui/Badge";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { CorrectionButton } from "../feedback/CorrectionButton";
import { useFindingDismiss } from "../../hooks/useFindingDismiss";
import type { TaskFinding } from "../../../../shared/types";

interface FindingCardProps {
  finding: TaskFinding;
  brandId?: string;
}

function typeVariant(type: TaskFinding["type"]) {
  switch (type) {
    case "content_idea":
      return "info";
    case "action_item":
      return "success";
    case "opportunity":
      return "info";
    case "metric":
      return "default";
    case "threat":
      return "error";
    default:
      return "default";
  }
}

function statusVariant(status: TaskFinding["status"]) {
  switch (status) {
    case "acted":
      return "success";
    case "dismissed":
      return "default";
    case "new":
    default:
      return "warning";
  }
}

function scoreLabel(score: number | null | undefined) {
  return typeof score === "number" && Number.isFinite(score) ? `${score}/100` : null;
}

function stringMetadataValue(metadata: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = metadata?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

function trustVariant(kind: NonNullable<TaskFinding["trust_issues"]>[number]["kind"]) {
  switch (kind) {
    case "retrieval":
      return "warning";
    case "source":
      return "error";
    case "synthesis":
      return "info";
    default:
      return "default";
  }
}

export function FindingCard({ finding, brandId }: FindingCardProps) {
  const [status, setStatus] = useState(finding.status);
  const dismissMutation = useFindingDismiss(finding.run_id);
  const hasPersistedId = !finding.id.startsWith("synthetic:");
  const bodyPreview =
    finding.body && finding.body.length > 140
      ? `${finding.body.slice(0, 140).trimEnd()}...`
      : finding.body;
  const bodyKind = stringMetadataValue(finding.metadata, "body_kind");
  const snippetOnly = bodyKind === "snippet";

  useEffect(() => {
    setStatus(finding.status);
  }, [finding.id, finding.status]);

  async function toggleDismiss() {
    const nextStatus = status === "dismissed" ? "new" : "dismissed";
    const result = await dismissMutation.mutateAsync({ findingId: finding.id, status: nextStatus });
    setStatus(result.data.status);
  }

  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={typeVariant(finding.type)}>{finding.type}</Badge>
            <Badge variant={statusVariant(status)} data-testid="finding-status">{status}</Badge>
            {scoreLabel(finding.score) && (
              <span className="text-xs font-medium text-slate-500">{scoreLabel(finding.score)}</span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-slate-900">{finding.title}</h3>
        </div>
        <span className="text-xs text-slate-500">{new Date(finding.created_at).toLocaleString()}</span>
      </div>

      {(finding.provenance?.query || finding.provenance?.source_url || finding.provenance?.domain) && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
          {finding.provenance?.query && (
            <span data-testid="finding-query">Query: <span className="text-slate-700">{finding.provenance.query}</span></span>
          )}
          {finding.provenance?.source_type && (
            <span>Source: <span className="text-slate-700">{finding.provenance.source_type}</span></span>
          )}
          {finding.provenance?.domain && (
            <span>Domain: <span className="text-slate-700">{finding.provenance.domain}</span></span>
          )}
          {finding.provenance?.published_at && (
            <span>Published: <span className="text-slate-700">{new Date(finding.provenance.published_at).toLocaleDateString()}</span></span>
          )}
          {finding.provenance?.source_url && (
            <a
              href={finding.provenance.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Open source
            </a>
          )}
        </div>
      )}

      {(finding.trust_issues ?? []).length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex flex-wrap gap-2">
            {finding.trust_issues?.map((issue) => (
              <Badge key={`${finding.id}-${issue.code}`} variant={trustVariant(issue.kind)}>
                {issue.kind}
              </Badge>
            ))}
          </div>
          <div className="mt-2 space-y-1">
            {finding.trust_issues?.map((issue) => (
              <p key={issue.code} className="text-xs text-slate-700">
                <span className="font-medium">{issue.label}:</span> {issue.detail}
              </p>
            ))}
          </div>
        </div>
      )}

      <details className="group rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <summary className="cursor-pointer list-none text-sm font-medium text-slate-700">
          <span className="group-open:hidden">Show body</span>
          <span className="hidden group-open:inline">Hide body</span>
        </summary>
        {snippetOnly && (
          <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800" data-testid="finding-body-kind">
            Snippet-only evidence captured from the source collector.
          </p>
        )}
        <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-700" data-testid="finding-body">
          {finding.body ?? "No body recorded."}
        </p>
      </details>

      {bodyPreview && bodyPreview !== finding.body && (
        <p className="text-sm text-slate-600" data-testid="finding-preview">
          {bodyPreview}
        </p>
      )}

      <details className="rounded-lg border border-slate-200 px-3 py-2">
        <summary className="cursor-pointer list-none text-sm font-medium text-slate-700">
          Metadata
        </summary>
        <pre className="mt-2 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
          {JSON.stringify(finding.metadata ?? {}, null, 2)}
        </pre>
      </details>

      {brandId && hasPersistedId && (
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" loading={dismissMutation.isPending} onClick={() => void toggleDismiss()}>
            {status === "dismissed" ? "Undo dismiss" : "Dismiss"}
          </Button>
          <CorrectionButton
            brandId={brandId}
            sourceType="finding"
            sourceId={finding.id}
            contextHint={`${finding.title}${finding.body ? ` — ${finding.body.slice(0, 200)}` : ""}`}
          />
        </div>
      )}

      {brandId && !hasPersistedId && (
        <p className="text-xs text-amber-700">
          Legacy finding record: feedback actions are unavailable until this finding is re-ingested with a persistent id.
        </p>
      )}
    </Card>
  );
}
